import {
    createReadExcelTool,
} from "@carousel-office/agent-runtime";
import { ToolBase } from "../../../base/toolBase";
import { safeParseToolParams } from "../../../base/toolDescription";
import {
    ReadExcelParamsSchema,
    type ReadExcelParams,
} from "@carousel-office/agent-runtime/excelEditor/tools/readExcel/readExcel.schema";
import {
    ReadExcelTool as LegacyReadExcelTool,
} from "@carousel-office/agent-runtime/excelEditor/tools/readExcel/readExcel.tool";
import type {
    ReadExcelResponse,
    ReadExcelToolConfig,
    ReadResult,
} from "@carousel-office/agent-runtime/excelEditor/tools/readExcel/readExcel.types";
import { getContextResolver } from "../../../../core/contextResolver";
import { BackendSessionExcelRunner } from "../../runtime/backendSessionExcelRunner";
import { EXCEL_BACKEND_URL } from "@carousel-office/shared";

function isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

function toReadResultArray(value: unknown): ReadResult[] {
    return Array.isArray(value) ? (value as ReadResult[]) : [];
}

export class ReadExcelTool extends ToolBase<ReadExcelParams, ReadExcelResponse> {
    public readonly name = "readExcel";
    public readonly description;
    public readonly systemPromptDescription;

    fieldsToCheck = [];
    listsToCheck = [];

    private readonly legacyDelegate: LegacyReadExcelTool;
    private readonly runner: BackendSessionExcelRunner;
    private readonly mode: "session" | "filepath";
    private sessionId: string | null = null;

    constructor(config: ReadExcelToolConfig = { mode: "session" }) {
        super();
        this.mode = config.mode;
        this.runner = new BackendSessionExcelRunner(config.excelBackendUrl || EXCEL_BACKEND_URL);
        this.legacyDelegate = new LegacyReadExcelTool(config);
        this.description = this.legacyDelegate.description;
        this.systemPromptDescription = this.legacyDelegate.systemPromptDescription;
        this.fieldsToCheck = [...this.legacyDelegate.fieldsToCheck];
        this.listsToCheck = [...this.legacyDelegate.listsToCheck];
    }

    public setSessionId(sessionId: string): void {
        this.sessionId = sessionId;
        this.legacyDelegate.setSessionId(sessionId);
    }

    public clearFilepathCache(): void {
        this.legacyDelegate.clearFilepathCache();
    }

    public async execute(parameters: unknown): Promise<ReadExcelResponse> {
        if (this.mode === "filepath") {
            return this.legacyDelegate.execute(parameters as any);
        }

        const parseResult = safeParseToolParams(ReadExcelParamsSchema, parameters);
        if (!parseResult.success) {
            throw new Error(parseResult.error);
        }
        if (!this.sessionId) {
            throw new Error("No active session. The agent must create a session first.");
        }

        const runtimeTool = createReadExcelTool(this.runner, this.sessionId);
        const runtimeResult = await runtimeTool.execute({
            ...parseResult.data,
            sessionId: this.sessionId,
        } as any);

        const payload = isRecord(runtimeResult.data) ? runtimeResult.data : {};
        const imageIds: string[] = [];
        const contextResolver = getContextResolver();

        const readFormatting = toReadResultArray(payload.readFormatting);
        for (const item of readFormatting) {
            const formatItem: Record<string, unknown> = isRecord(item) ? item : {};
            const imageBase64 = formatItem["range_image_base64"];
            if (typeof imageBase64 === "string" && imageBase64.length > 0) {
                const sheet =
                    typeof formatItem["sheet_name"] === "string" ? (formatItem["sheet_name"] as string) : "sheet";
                const range =
                    typeof formatItem["target_range"] === "string"
                        ? (formatItem["target_range"] as string)
                        : "range";
                const imageId = `format_read_${sheet}_${range.replace(/[!:]/g, "_")}_${Date.now()}`;
                const processed = await contextResolver.storeImageAsync(imageId, imageBase64, "image/png");
                imageIds.push(imageId);
                console.log(`[ReadExcelTool] Stored formatting image: ${imageId}${processed.wasResized ? " (resized)" : ""}`);
            }
        }

        const listCharts = toReadResultArray(payload.listCharts);
        const normalizedReadCharts = toReadResultArray(payload.readCharts);

        for (const item of normalizedReadCharts) {
            const chartItem: Record<string, unknown> = isRecord(item) ? item : {};

            const chartImageSingle = chartItem["chart_image_base64"] ?? chartItem["chartImageBase64"];
            if (typeof chartImageSingle === "string" && chartImageSingle.length > 0) {
                const chartLabel =
                    (typeof chartItem["chartName"] === "string" && chartItem["chartName"]) ||
                    (typeof chartItem["chart_name"] === "string" && chartItem["chart_name"]) ||
                    (typeof chartItem["chartId"] === "string" && chartItem["chartId"]) ||
                    (typeof chartItem["chart_id"] === "string" && chartItem["chart_id"]) ||
                    "chart";
                const imageId = `chart_read_${String(chartLabel).replace(/[!:\s]/g, "_")}_${Date.now()}`;
                const processed = await contextResolver.storeImageAsync(imageId, chartImageSingle, "image/png");
                imageIds.push(imageId);
                console.log(`[ReadExcelTool] Stored chart image: ${imageId}${processed.wasResized ? " (resized)" : ""}`);
            }

            const chartImages = chartItem["chart_images"];
            if (!isRecord(chartImages)) continue;
            for (const [chartName, base64] of Object.entries(chartImages)) {
                if (typeof base64 !== "string" || !base64) continue;
                const imageId = `chart_read_${chartName.replace(/[!:\s]/g, "_")}_${Date.now()}`;
                const processed = await contextResolver.storeImageAsync(imageId, base64, "image/png");
                imageIds.push(imageId);
                console.log(`[ReadExcelTool] Stored chart image: ${imageId}${processed.wasResized ? " (resized)" : ""}`);
            }
        }

        const response: ReadExcelResponse = {
            success: runtimeResult.success,
            results: {
                readContent: toReadResultArray(payload.readContent),
                readFormatting,
                readCharts: normalizedReadCharts,
                listCharts,
                formulaTrace: toReadResultArray(payload.formulaTrace),
                tableAnalysis: toReadResultArray(payload.tableAnalysis),
                listSheets: toReadResultArray(payload.listSheets),
            },
            errors: runtimeResult.errors,
            _imageIds: imageIds.length > 0 ? imageIds : undefined,
            _excelReadInfos: runtimeResult._excelReadInfos,
        };
        return response;
    }
}
